import { useMemo, useState } from 'react';
import type { AthenaHiloEnLista } from '@conversia/shared-types';
import { Check, MessagesSquare, MoreHorizontal, Pencil, Search, SquarePen, Trash2, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { agruparHilos, filtrarHilos, type GrupoDeHilos } from '@/lib/athena-hilos';

/**
 * EL HISTORIAL DE ATHENA (tanda 6): la columna de la derecha. Kervens: «debe tener un historial
 * a mano derecha… que lleve historial». Las conversaciones por fecha, un buscador y, en cada
 * una, renombrar y borrar.
 *
 * El «⋯» de cada conversación NO abre un menú flotante: despliega sus dos opciones debajo de la
 * fila. La lista se desplaza por dentro, y un menú flotante ahí queda recortado o hay que
 * perseguirlo por la pantalla; desplegado en el sitio, además, se toca igual con el dedo.
 */

const FOCO = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-600';

/** Qué está desplegado en la lista: las opciones de un hilo, su nombre en edición o la pregunta de borrar. */
export interface MenuDeHilo {
  id: string;
  modo: 'menu' | 'renombrar' | 'borrar';
}

export interface ListaDeHilosProps {
  grupos: ReadonlyArray<GrupoDeHilos<AthenaHiloEnLista>>;
  /** El hilo abierto en la conversación. */
  activo: string | null;
  menu?: MenuDeHilo | null;
  /** Lo que se dice cuando no hay nada que listar. */
  vacio?: string;
  onElegir?: (id: string) => void;
  onMenu?: (menu: MenuDeHilo | null) => void;
  onRenombrar?: (id: string, titulo: string) => void;
  onBorrar?: (id: string) => void;
}

const OPCION = cn('inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium transition-colors md:min-h-[32px]', FOCO);
const CUADRADO = cn('grid h-11 w-11 shrink-0 place-items-center rounded-lg transition-colors md:h-8 md:w-8', FOCO);

/** Los hilos, agrupados. Sin estado: qué está desplegado se lo dicen (`menu`). */
export function ListaDeHilos({ grupos, activo, menu = null, vacio, onElegir, onMenu, onRenombrar, onBorrar }: ListaDeHilosProps) {
  if (grupos.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-sm leading-relaxed text-slate-500 dark:text-slate-400">
        {/* El dibujo solo cuando de verdad no hay nada: en una búsqueda sin resultados basta la frase. */}
        {!vacio && (
          <span aria-hidden className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-slate-200/70 text-slate-400 dark:bg-slate-800 dark:text-slate-500">
            <MessagesSquare className="h-5 w-5" />
          </span>
        )}
        <p>{vacio ?? 'Aquí van a quedar tus conversaciones con Athena.'}</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {grupos.map((grupo) => (
        <section key={grupo.id} aria-label={grupo.titulo}>
          <h3 className="px-3 pb-1 text-xs font-semibold text-slate-500 dark:text-slate-400">{grupo.titulo}</h3>
          <ul className="space-y-0.5">
            {grupo.hilos.map((hilo) => {
              const esActivo = hilo.id === activo;
              const suMenu = menu?.id === hilo.id ? menu.modo : null;

              if (suMenu === 'renombrar') {
                return (
                  <li key={hilo.id}>
                    <form
                      className="flex items-center gap-1 rounded-lg bg-surface p-1 shadow-sm ring-1 ring-brand-300 dark:ring-brand-500/60"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const titulo = String(new FormData(e.currentTarget).get('titulo') ?? '').trim();
                        // Sin nombre o con el mismo, no hay nada que guardar.
                        if (titulo && titulo !== hilo.titulo) onRenombrar?.(hilo.id, titulo);
                        onMenu?.(null);
                      }}
                    >
                      <input
                        name="titulo"
                        defaultValue={hilo.titulo}
                        maxLength={80}
                        autoFocus
                        aria-label="Nombre de la conversación"
                        onFocus={(e) => e.currentTarget.select()}
                        onKeyDown={(e) => e.key === 'Escape' && onMenu?.(null)}
                        className="h-11 min-w-0 flex-1 rounded-md bg-transparent px-2 text-base text-slate-900 outline-none md:h-8 md:text-sm dark:text-slate-100"
                      />
                      <button type="submit" aria-label="Guardar el nombre" className={cn(CUADRADO, 'bg-brand-600 text-white hover:bg-brand-700')}>
                        <Check className="h-4 w-4" />
                      </button>
                      <button type="button" aria-label="Cancelar" onClick={() => onMenu?.(null)} className={cn(CUADRADO, 'text-slate-500 hover:bg-slate-200/70 dark:text-slate-400 dark:hover:bg-slate-700')}>
                        <X className="h-4 w-4" />
                      </button>
                    </form>
                  </li>
                );
              }

              return (
                <li
                  key={hilo.id}
                  data-hilo={hilo.id}
                  className={cn(
                    'rounded-lg transition-colors',
                    esActivo ? 'bg-surface shadow-sm ring-1 ring-surface-border' : suMenu ? 'bg-slate-200/60 dark:bg-slate-800/70' : 'hover:bg-slate-200/60 dark:hover:bg-slate-800/70',
                  )}
                >
                  <div className="flex items-center">
                    <button
                      type="button"
                      aria-current={esActivo ? 'true' : undefined}
                      onClick={() => onElegir?.(hilo.id)}
                      className={cn('min-h-[52px] min-w-0 flex-1 rounded-lg py-2 pl-3 pr-1 text-left', FOCO)}
                    >
                      <span className={cn('block truncate text-sm', esActivo ? 'font-semibold text-brand-700 dark:text-brand-200' : 'font-medium text-slate-800 dark:text-slate-100')}>
                        {hilo.titulo}
                      </span>
                      {/* La vista previa llega con los asteriscos de las negritas de Athena (y a veces cortada a la mitad de una): aquí sobran. */}
                      <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{hilo.vistaPrevia.replace(/\*\*/g, '') || 'Sin mensajes todavía'}</span>
                    </button>
                    <button
                      type="button"
                      aria-label={`Opciones de «${hilo.titulo}»`}
                      aria-expanded={suMenu !== null}
                      onClick={() => onMenu?.(suMenu ? null : { id: hilo.id, modo: 'menu' })}
                      className={cn(CUADRADO, 'mr-1 text-slate-500 hover:bg-slate-300/60 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100')}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </div>

                  {suMenu === 'menu' && (
                    <div className="flex items-center gap-1 px-1.5 pb-1.5 motion-safe:animate-fade-in">
                      <button type="button" onClick={() => onMenu?.({ id: hilo.id, modo: 'renombrar' })} className={cn(OPCION, 'text-slate-700 hover:bg-slate-300/60 dark:text-slate-200 dark:hover:bg-slate-700')}>
                        <Pencil aria-hidden className="h-3.5 w-3.5" />
                        Renombrar
                      </button>
                      <button type="button" onClick={() => onMenu?.({ id: hilo.id, modo: 'borrar' })} className={cn(OPCION, 'text-red-700 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-950/60')}>
                        <Trash2 aria-hidden className="h-3.5 w-3.5" />
                        Borrar
                      </button>
                    </div>
                  )}
                  {suMenu === 'borrar' && (
                    <div role="group" aria-label="¿Borrar esta conversación?" className="px-3 pb-2 motion-safe:animate-fade-in">
                      <p className="text-xs leading-snug text-slate-600 dark:text-slate-300">¿Borrar esta conversación? No se puede deshacer.</p>
                      <div className="mt-1.5 flex items-center gap-1.5">
                        <button type="button" onClick={() => onBorrar?.(hilo.id)} className={cn(OPCION, 'bg-red-600 text-white hover:bg-red-700')}>
                          Sí, borrar
                        </button>
                        {/* El foco cae en «No»: un Enter de más no borra una conversación. */}
                        <button type="button" autoFocus onClick={() => onMenu?.(null)} className={cn(OPCION, 'text-slate-700 hover:bg-slate-300/60 dark:text-slate-200 dark:hover:bg-slate-700')}>
                          No
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

export interface HistorialDeAthenaProps {
  /** `undefined` mientras carga. */
  hilos: AthenaHiloEnLista[] | undefined;
  /** No se pudo cargar: se dice, con cómo volver a intentar. */
  fallo?: boolean;
  onReintentar?: () => void;
  activo: string | null;
  /** El «ahora» con el que se reparte en Hoy, Ayer… */
  ahora: Date;
  onElegir: (id: string) => void;
  onNueva: () => void;
  onRenombrar: (id: string, titulo: string) => void;
  onBorrar: (id: string) => void;
  /** Solo en el teléfono, donde es un panel que tapa la conversación: cómo cerrarlo. */
  onCerrar?: () => void;
  className?: string;
}

/** La columna entera: título, «Nueva conversación», buscador y la lista. */
export function HistorialDeAthena({ hilos, fallo = false, onReintentar, activo, ahora, onElegir, onNueva, onRenombrar, onBorrar, onCerrar, className }: HistorialDeAthenaProps) {
  const [busqueda, setBusqueda] = useState('');
  const [menu, setMenu] = useState<MenuDeHilo | null>(null);
  const grupos = useMemo(() => agruparHilos(filtrarHilos(hilos ?? [], busqueda), ahora), [hilos, busqueda, ahora]);
  const buscando = busqueda.trim();

  return (
    <aside aria-label="Historial de conversaciones" className={cn('min-h-0 flex-col border-l border-surface-border bg-surface-muted', className)}>
      <div className="flex h-14 shrink-0 items-center gap-1 border-b border-surface-border pl-4 pr-2">
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900 dark:text-slate-50">Historial</h2>
        <button
          type="button"
          onClick={() => {
            setMenu(null);
            onNueva();
          }}
          className={cn('inline-flex h-11 items-center gap-1.5 rounded-lg px-2.5 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-50 md:h-9 dark:text-brand-200 dark:hover:bg-brand-900/40', FOCO)}
        >
          <SquarePen aria-hidden className="h-4 w-4" />
          Nueva conversación
        </button>
        {onCerrar && (
          <button type="button" onClick={onCerrar} aria-label="Cerrar el historial" className={cn(CUADRADO, 'text-slate-500 hover:bg-slate-200/70 md:hidden dark:text-slate-400 dark:hover:bg-slate-800')}>
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="shrink-0 p-3">
        <div className="relative">
          <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            aria-label="Buscar en el historial"
            placeholder="Buscar…"
            className="h-11 w-full rounded-lg border border-surface-border bg-surface pl-9 pr-3 text-base text-slate-900 placeholder:text-slate-500 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 md:h-9 md:text-sm dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-400"
          />
        </div>
      </div>

      <nav aria-label="Conversaciones" className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {fallo ? (
          <div className="px-3 py-8 text-center text-sm text-slate-600 dark:text-slate-300">
            <p>No pude cargar el historial.</p>
            <button type="button" onClick={onReintentar} className={cn(OPCION, 'mt-1 text-brand-700 hover:bg-brand-50 dark:text-brand-200 dark:hover:bg-brand-900/40')}>
              Reintentar
            </button>
          </div>
        ) : hilos === undefined ? (
          <div aria-hidden className="space-y-3 px-3 pt-2">
            {[72, 56, 64, 48].map((ancho) => (
              <div key={ancho} className="space-y-1.5 motion-safe:animate-pulse">
                <div className="h-3 rounded bg-slate-200 dark:bg-slate-800" style={{ width: `${ancho}%` }} />
                <div className="h-2.5 w-11/12 rounded bg-slate-200/70 dark:bg-slate-800/70" />
              </div>
            ))}
          </div>
        ) : (
          <ListaDeHilos
            grupos={grupos}
            activo={activo}
            menu={menu}
            vacio={buscando ? `Nada con "${buscando}".` : undefined}
            onElegir={(id) => {
              setMenu(null);
              onElegir(id);
            }}
            onMenu={setMenu}
            onRenombrar={onRenombrar}
            onBorrar={(id) => {
              setMenu(null);
              onBorrar(id);
            }}
          />
        )}
      </nav>
    </aside>
  );
}
